import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import backendVerifiedUser from '../../api/backendVerifiedUser';
import { usePermissions } from '../../permissions/PermissionContext';
import ChatBody from './ChatBody';
import useAssistantChat from './useAssistantChat';

const STORAGE_KEY = 'aiAssistantWidgetChat'; // keeps the popup's chat across page changes

const readStoredChat = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};
const storeChat = (id) => {
  try {
    if (id) sessionStorage.setItem(STORAGE_KEY, String(id));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
};

// Chat button at the bottom-right of every admin page; opens a small AI Assistant chat.
export default function FloatingAssistant() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { canOpen } = usePermissions();
  const allowed = canOpen('/ai-assistant');
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [restored, setRestored] = useState(false);
  const chat = useAssistantChat({ onRunEnd: storeChat });

  useEffect(() => {
    if (!allowed) return;
    backendVerifiedUser
      .get('/ai-assistant/status')
      .then((res) => setStatus(res.data.success))
      .catch(() => setStatus({ is_active: false }));
  }, [allowed]);

  // First open: continue the chat that was open before a page change
  useEffect(() => {
    if (!open || restored) return;
    setRestored(true);
    const id = readStoredChat();
    if (id && !chat.activeId) chat.openConversation(id).catch(() => storeChat(null));
  }, [open, restored]);

  useEffect(() => {
    if (open) setTimeout(() => chat.inputRef.current?.focus(), 50);
  }, [open]);

  if (!allowed || pathname.startsWith('/ai-assistant')) return null;

  const inactive = status && !status.is_active;

  const newChat = () => {
    if (chat.busy) return;
    chat.newChat();
    storeChat(null);
  };

  const openFullPage = () => {
    setOpen(false);
    navigate(chat.activeId ? `/ai-assistant?chat=${chat.activeId}` : '/ai-assistant');
  };

  return (
    <>
      {open && (
        <div
          className="fixed z-[900] bottom-24 right-3 left-3 sm:left-auto sm:right-6 sm:w-[400px] h-[600px] max-h-[calc(100vh-120px)] flex flex-col bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden"
          role="dialog"
          aria-label="AI Assistant"
        >
          <div className="flex items-center gap-2 px-4 py-3 bg-orange-600 text-white">
            <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">C</div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold leading-tight">AI Assistant</div>
              <div className="text-[11px] text-white/80 leading-tight">{chat.busy ? 'Working…' : 'Powered by Claude'}</div>
            </div>
            <button onClick={newChat} disabled={chat.busy} title="New chat" className="p-1.5 rounded-lg hover:bg-white/15 disabled:opacity-50">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button onClick={openFullPage} title="Open full page" className="p-1.5 rounded-lg hover:bg-white/15">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
              </svg>
            </button>
            <button onClick={() => setOpen(false)} title="Close" className="p-1.5 rounded-lg hover:bg-white/15">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {inactive && (
            <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between gap-2">
              <span>{status.configured ? 'The Claude AI plugin is turned off.' : 'The Claude AI plugin is not set up yet.'}</span>
              {canOpen('/plugins/claude') && (
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate('/plugins/claude');
                  }}
                  className="shrink-0 font-semibold underline"
                >
                  Set up
                </button>
              )}
            </div>
          )}

          <ChatBody chat={chat} inactive={inactive} compact />
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        title={open ? 'Close AI Assistant' : 'AI Assistant'}
        aria-label={open ? 'Close AI Assistant' : 'Open AI Assistant'}
        className="fixed z-[900] bottom-6 right-6 w-14 h-14 rounded-full bg-orange-600 hover:bg-orange-700 text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-all"
      >
        {open ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.83L3 20l1.4-3.72A7.6 7.6 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        )}
        {!open && (chat.pending || chat.busy) && (
          <span className={`absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${chat.pending ? 'bg-amber-400' : 'bg-blue-400 animate-pulse'}`} />
        )}
      </button>
    </>
  );
}
